import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { RootState } from '@share/utils';
import { RewardWalletPriority } from '@share/common-types';
import { Routes } from '@share/constants';
import { getNextRewardByPriority } from '@utils';

import './style.scss';

function formatRewardValue(
  value: number | undefined,
  account: { walletNoDecimals?: boolean } | null | undefined
): string {
  if (value == null) return '—';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: account?.walletNoDecimals ? 0 : 2,
    maximumFractionDigits: account?.walletNoDecimals ? 0 : 2,
  })}`;
}

interface IProps {
  /** Priority for picking the "next" reward. Defaults to Cheapest. */
  priority?: RewardWalletPriority;
}

export function WalletNextRewardCard(props: IProps) {
  const { priority = RewardWalletPriority.Cheapest } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { account, userWalletReward } = loginStore;
  const allRewards = userWalletReward?.allRewards ?? [];

  const nextReward = getNextRewardByPriority(
    allRewards,
    priority === RewardWalletPriority.Highest || priority === RewardWalletPriority.Cheapest ? priority : RewardWalletPriority.Cheapest
  );

  if (nextReward == null) {
    return null;
  }

  const expirationMoment = nextReward.expirationDate ? moment(nextReward.expirationDate) : null;
  const expirationFormatted = expirationMoment?.isValid() ? expirationMoment.format('MM/DD/YYYY') : '—';
  const daysLeft =
    expirationMoment?.isValid()
      ? Math.max(0, expirationMoment.diff(moment(), 'days'))
      : null;
  const urgencyLevel = null;// daysLeft != null ? (daysLeft <= 7 ? 'urgent' : daysLeft <= 30 ? 'soon' : null) : null;
  const amountFormatted = formatRewardValue(nextReward.rewardValue, account);
  const learnHref = account?.name ? `/${account.name}${Routes.RewardsLearn}` : null;

  return (
    <div className={`wallet-next-reward-card ${urgencyLevel ? `wallet-next-reward-card--${urgencyLevel}` : ''}`}>
      <FontAwesomeIcon icon={faGift} className="wallet-next-reward-card__icon" />
      <div className="wallet-next-reward-card__content">
        <div className="wallet-next-reward-card__header">
          <span className="wallet-next-reward-card__title">
            <FormattedMessage id="wallet.reward.next_available" />
            {learnHref && (
              <a
                href={learnHref}
                className="wallet-next-reward-card__learn-link"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: '20px' }}
              >
                <FormattedMessage id="wallet.reward.learn_how" />
              </a>)}
          </span>
        </div>
        <div className="wallet-next-reward-card__row wallet-next-reward-card__amount-expiry">
          <div className="wallet-next-reward-card__amount-col">
            <span className="wallet-next-reward-card__label">
              <FormattedMessage id="wallet.reward.amount" />:
            </span>
            <span className="wallet-next-reward-card__value">{amountFormatted}</span>
          </div>
          <div className={`wallet-next-reward-card__expiry-col ${urgencyLevel ? `wallet-next-reward-card__expiry-col--${urgencyLevel}` : ''}`}>
            {daysLeft != null ? (
              <>
                <span className="wallet-next-reward-card__label">
                  <FormattedMessage id="rewards.next.expiresIn" values={{ days: daysLeft }} />
                </span>
                <span className="wallet-next-reward-card__value">
                  <FormattedMessage id="rewards.next.useBefore" values={{ date: expirationFormatted }} />
                </span>
              </>
            ) : (
              <>
                <span className="wallet-next-reward-card__label">
                  <FormattedMessage id="wallet.reward.expiration_date" />:
                </span>
                <span className="wallet-next-reward-card__value">{expirationFormatted}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
